import { Badge } from "@hortis/ui/badge";
import { Button, IconButton } from "@hortis/ui/button";
import { DialogTrigger } from "@hortis/ui/dialog";
import { useMediaQuery } from "@hortis/ui/hooks/media-query";
import { Grid3 } from "@hortis/ui/icons";
import { Tooltip, TooltipContent, TooltipTrigger } from "@hortis/ui/tooltip";
import { sub } from "date-fns/esm";
import {
  MaterialFieldsFragment,
  MaterialGroup,
  PlantMaterialStatus,
} from "generated/graphql";
import TimeAgo, { Formatter } from "react-timeago";
import {
  RecordSection,
  RecordSectionTitle,
} from "src/components/record-section";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import { CreateObservationDialog } from "src/features/material-page/create-observation-dialog/create-observation-dialog";
import { ObservationContent } from "src/features/material-page/sections/material-record-observations/observation-content";
import { twMerge } from "tailwind-merge";

const overdueFormatter: Formatter = (value, unit, suffix) => {
  const isDueToday = new Set(["second", "hour", "minute"]);
  return isDueToday.has(unit) || (unit === "day" && value < 1)
    ? "Due today"
    : `${suffix === "ago" ? "Due" : ""} ${value} ${unit}${
        value > 1 ? "s" : ""
      } ${suffix}`;
};

export const MaterialLastObservationSection = ({
  material,
  className,
  compact = false,
}: {
  material: MaterialFieldsFragment;
  className?: string;
  compact?: boolean;
}) => {
  const hasData = material.lastObservation != null;
  const initialValues = {
    condition: material.lastObservation?.condition,
    followUp: material.followUp,
  };
  const isDesktop = useMediaQuery("lg");
  const fullSize = isDesktop && !compact;

  return hasData ? (
    <RecordSection
      className={twMerge(
        "gap-0 group pb-3",
        !hasData && "border-dashed shadow-none",
        className,
        !fullSize && "px-6",
      )}
      data-cy="last-observation-section"
    >
      <RecordSectionTitle>
        <div className="flex items-center gap-3">
          Last observation{" "}
          {material.daysOverdueFollowUp != null &&
            material.daysOverdueFollowUp > 0 && (
              <Badge
                testId="observation-overdue-badge"
                color="warning"
                className="border border-warning-200"
              >
                <div className="flex items-center gap-1">
                  {fullSize ? (
                    <TimeAgo
                      date={sub(new Date(), {
                        days: material.daysOverdueFollowUp,
                      })}
                      formatter={overdueFormatter}
                      style={{ display: "inline-block" }}
                    />
                  ) : (
                    "Overdue"
                  )}
                </div>
              </Badge>
            )}
        </div>
        {material.status !== PlantMaterialStatus.Absent &&
          material.materialGroup === MaterialGroup.Plant &&
          !material.archived && (
            <Tooltip>
              <CreateObservationDialog
                materialId={material.id}
                initialValues={initialValues}
              >
                {fullSize ? (
                  <DialogTrigger>
                    <Button
                      startIcon={Grid3}
                      size="sm"
                      variant="tertiaryGray"
                      data-cy="new-observation-button"
                    >
                      New observation
                    </Button>
                  </DialogTrigger>
                ) : (
                  <TooltipTrigger asChild onFocusCapture={stopEventPropagation}>
                    <DialogTrigger asChild>
                      <IconButton
                        icon={Grid3}
                        size="sm"
                        variant="tertiaryGray"
                        ariaLabel="New observation"
                        data-cy="new-observation-button"
                      />
                    </DialogTrigger>
                  </TooltipTrigger>
                )}
              </CreateObservationDialog>
              <TooltipContent side="left">New observation</TooltipContent>
            </Tooltip>
          )}
      </RecordSectionTitle>
      {material.lastObservation != null && (
        <ObservationContent observation={material.lastObservation} />
      )}
    </RecordSection>
  ) : (
    <RecordSection
      className={twMerge(
        "gap-1 border-dashed shadow-none pb-3 flex items-center justify-center",
        className,
      )}
    >
      {material.status !== PlantMaterialStatus.Absent &&
      material.materialGroup === MaterialGroup.Plant &&
      !material.archived ? (
        <CreateObservationDialog
          materialId={material.id}
          initialValues={initialValues}
        >
          <DialogTrigger asChild>
            <Button
              startIcon={Grid3}
              size="sm"
              variant="tertiaryGray"
              data-cy="first-observation-button"
            >
              Add first observation
            </Button>
          </DialogTrigger>
        </CreateObservationDialog>
      ) : (
        "No observations"
      )}
    </RecordSection>
  );
};
